import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  MessageCircle,
  Star,
  Heart,
  Users,
  Sparkles,
  ChevronDown,
  Menu,
  X,
  Quote,
  ArrowRight,
  Moon,
  CheckCircle,
  Mail,
  Facebook,
  Instagram,
  Copy,
  Check,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

// Contact Information
const CONTACT = {
  name: "Maulana Zakir Ali",
  title: "Islamic Spiritual Consultant & Advisor",
  email: "tantreekmaulanazakirali@gmail.com",
  facebook: "https://www.facebook.com/maulanazakirali",
  instagram: "https://www.instagram.com/zaki.rali0786/",
  numbers: [
    // {
    //   country: "UAE",
    //   flag: "🇦🇪",
    //   code: "+971",
    //   raw: "971582589260",
    //   display: "+971 58 258 9260",
    // },
    {
      country: "India",
      flag: "🇮🇳",
      code: "+91",
      raw: "919876624709",
      display: "+91 98766 24709",
    },
  ],
};

// ─── Contact Picker Modal ────────────────────────────────────────────────────
const ContactPickerModal = ({ mode, onClose }) => {
  // mode: "call" | "whatsapp"
  const [copied, setCopied] = useState(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.target === overlayRef.current) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const handleCopy = (display, idx) => {
    navigator.clipboard.writeText(display);
    setCopied(idx);
    setTimeout(() => setCopied(null), 1800);
  };

  const handleAction = (num) => {
    if (mode === "call") {
      window.location.href = `tel:+${num.raw}`;
    } else {
      window.open(
        `https://wa.me/${num.raw}?text=Assalam%20Alaikum%2C%20I%20need%20your%20guidance`,
        "_blank",
        "noopener,noreferrer",
      );
    }
    onClose();
  };

  const isCall = mode === "call";
  const Icon = isCall ? Phone : MessageCircle;
  const label = isCall ? "Call" : "WhatsApp";
  const accentColor = isCall ? "text-primary" : "text-[#25D366]";
  const borderColor = isCall
    ? "border-primary/30 hover:border-primary/70"
    : "border-[#25D366]/30 hover:border-[#25D366]/70";
  const bgIcon = isCall ? "bg-primary/15" : "bg-[#25D366]/15";
  const iconColor = isCall ? "text-primary" : "text-[#25D366]";

  return (
    <AnimatePresence>
      <motion.div
        ref={overlayRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center px-4"
        style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.88, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.88, y: 24 }}
          transition={{ type: "spring", stiffness: 340, damping: 28 }}
          className="relative w-full max-w-sm bg-card border border-white/10 rounded-2xl p-6 shadow-2xl"
          style={{ boxShadow: "0 0 60px rgba(0,0,0,0.6)" }}
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div
              className={`w-10 h-10 rounded-full ${bgIcon} flex items-center justify-center`}
            >
              <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div>
              <p className="font-heading text-foreground font-medium">
                {label} Now
              </p>
              <p className="text-xs text-muted-foreground">
                Choose your region
              </p>
            </div>
          </div>

          {/* Number cards */}
          <div className="space-y-3">
            {CONTACT.numbers.map((num, idx) => (
              <div
                key={idx}
                className={`group relative flex items-center gap-4 border ${borderColor} rounded-xl p-4 transition-all cursor-default mt-2`}
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                {/* Flag + info */}
                <span className="text-3xl leading-none select-none">
                  {num.flag}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">
                    {num.country} · {num.code}
                  </p>
                  <p className="font-mono text-sm font-medium text-foreground tracking-wide">
                    {num.display}
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2">
                  {/* Copy */}
                  <button
                    onClick={() => handleCopy(num.display, idx)}
                    title="Copy number"
                    className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  >
                    {copied === idx ? (
                      <Check className="w-3.5 h-3.5 text-green-400" />
                    ) : (
                      <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                    )}
                  </button>

                  {/* Main action */}
                  <button
                    onClick={() => handleAction(num)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      isCall
                        ? "bg-primary text-background hover:opacity-90"
                        : "bg-[#25D366] text-white hover:opacity-90"
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {label}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground text-center mt-5">
            Tap <span className={`font-medium ${accentColor}`}>{label}</span> to
            open directly · or copy the number
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Hook: use contact picker ────────────────────────────────────────────────
const useContactPicker = () => {
  const [modal, setModal] = useState(null); // "call" | "whatsapp" | null

  const openCall = (e) => {
    e && e.preventDefault();
    setModal("call");
  };
  const openWhatsApp = (e) => {
    e && e.preventDefault();
    setModal("whatsapp");
  };
  const close = () => setModal(null);

  const ModalComponent = modal ? (
    <ContactPickerModal mode={modal} onClose={close} />
  ) : null;

  return { openCall, openWhatsApp, ModalComponent };
};

// ─── Animation Variants ──────────────────────────────────────────────────────
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

// ─── Navbar ──────────────────────────────────────────────────────────────────
const Navbar = ({ openCall, openWhatsApp }) => {
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { label: "Home", href: "#home" },
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-white/5"
      data-testid="navbar"
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="#home"
            className="flex items-center gap-2"
            data-testid="logo"
          >
            <Moon className="w-6 h-6 text-primary crescent-glow" />
            <span className="font-heading text-lg md:text-xl text-primary">
              Nur-e-Hidayat
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="nav-link text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={openCall}
              className="call-btn flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-background transition-all"
              data-testid="nav-call-btn"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </button>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-foreground p-2"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden pb-4"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className="text-muted-foreground hover:text-primary transition-colors py-2"
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={(e) => {
                  setIsOpen(false);
                  openCall(e);
                }}
                className="call-btn flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-background"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

// ─── Hero ────────────────────────────────────────────────────────────────────
const Hero = ({ openCall, openWhatsApp }) => {
  return (
    <section
      id="home"
      className="hero-bg relative min-h-screen flex items-center pt-20"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 islamic-pattern" />
      <div className="absolute inset-0 star-pattern" />

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-left"
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary mb-4"
            >
              Spiritual Guidance & Islamic Consultation
            </motion.p>

            <motion.h1
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6"
            >
              Spiritual Guidance,
              <br />
              <span className="gold-shimmer">Based on Islamic Teachings</span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl"
            >
              Seek clarity through the wisdom of Islamic teachings and
              compassionate advice. {CONTACT.name} offers supportive
              consultation for life's most challenging moments.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={openWhatsApp}
                className="whatsapp-btn flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-semibold transition-all"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp Now
              </button>
              <button
                onClick={openCall}
                className="flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary/50 text-primary hover:bg-primary/10 transition-all font-medium"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </button>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="flex flex-wrap items-center gap-6 mt-10"
            >
              {["35+ Years Experience", "100% Confidential","Direct Consultation – No Middle Person"].map(
                (badge, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <CheckCircle className="w-4 h-4 text-primary" />
                    {badge}
                  </div>
                ),
              )}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-emerald-dark/50 rounded-2xl blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1762708551194-de4ef6baff9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxtdXNsaW0lMjBjb3VwbGUlMjB3ZWRkaW5nfGVufDB8fHx8MTc3MjQ2NTc3Nnww&ixlib=rb-4.1.0&q=85"
                alt="Muslim Couple Wedding"
                className="relative rounded-2xl w-full object-cover aspect-[4/5] border border-white/10"
                data-testid="hero-image"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-background/90 backdrop-blur-md rounded-xl p-4 border border-primary/20">
                <p className="font-heading text-primary text-lg">
                  {CONTACT.name}
                </p>
                <p className="text-sm text-muted-foreground">{CONTACT.title}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <a
          href="#about"
          className="flex flex-col items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </a>
      </motion.div>
    </section>
  );
};

// ─── Urgency Block ────────────────────────────────────────────────────────────
const UrgencyBlock = ({ openWhatsApp }) => (
  <section
    className="relative bg-primary/10 border-y border-primary/20 py-5 my-10"
    data-testid="urgency-block"
  >
    <div className="container mx-auto px-4 md:px-8">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="flex flex-col sm:flex-row items-center justify-between gap-4"
      >
        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="font-semibold text-primary">
              Limited Consultation Slots Available Today
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
            Get response within 10 minutes
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
            Talk directly with expert (no agent)
          </div>
        </div>
        <button
          onClick={openWhatsApp}
          className="whatsapp-btn flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-semibold whitespace-nowrap transition-all"
        >
          <MessageCircle className="w-4 h-4" />
          Chat Now
        </button>
      </motion.div>
    </div>
  </section>
);

const About = ({ openWhatsApp }) => {
  return (
    <section
      id="about"
      className="relative py-24 md:py-32 bg-card"
      data-testid="about-section"
    >
      <div className="absolute inset-0 islamic-pattern" />

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/10 rounded-2xl" />
              <img
                src="https://images.unsplash.com/photo-1764641237190-d979a7c8b1d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxpc2xhbWljJTIwbWFycmlhZ2UlMjBjZXJlbW9ueXxlbnwwfHx8fDE3NzI0NjU3Nzd8MA&ixlib=rb-4.1.0&q=85"
                alt="Maulana Zakir Ali"
                className="relative rounded-2xl w-full object-cover aspect-[4/5] border border-primary/20"
                data-testid="about-image"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 md:right-6 bg-primary text-background rounded-2xl p-6 gold-glow">
              <p className="font-heading text-4xl font-bold">35+</p>
              <p className="text-sm font-medium">Years of Experience</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary mb-4">
              About Us
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground mb-6">
              Guidance based on Islamic teachings and experience
            </h2>

            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                <span className="text-primary font-medium">{CONTACT.name}</span>{" "}
                has over 35 years of experience in providing Islamic spiritual
                consultation. Thousands of people have received guidance for
                relationship, marriage, and personal challenges through his
                compassionate and sincere approach.
              </p>
              <p className="text-sm italic text-muted-foreground/70">
                * Results may vary. This service provides spiritual guidance and
                consultation only.
              </p>
            </div>

            <div className="mt-8 space-y-4">
              {[
                "Direct Consultation",
                "Private & Confidential",
                "Based on Islamic Teachings",
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-3 mt-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-foreground">{feature}</p>
                </div>
              ))}
            </div>

            {/* Direct Consultation Banner */}
            {/* <div className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/40 bg-primary/10">
              <Star className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                Direct Consultation – No Middle Person
              </span>
            </div> */}

            <button
              onClick={openWhatsApp}
              className="whatsapp-btn inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold mt-8 transition-all"
              data-testid="about-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Consult Now
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ─── Services Data ───────────────────────────────────────────────────────────
const servicesData = [
  {
    title: "Marriage Consultation",
    description:
      "Receive thoughtful guidance for your marriage through Islamic teachings. Our consultations help bring clarity, harmony, and understanding to your relationship.",
    image:
      "https://images.unsplash.com/photo-1762708551197-24c74ea894b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxtdXNsaW0lMjBjb3VwbGUlMjB3ZWRkaW5nfGVufDB8fHx8MTc3MjQ2NTc3Nnww&ixlib=rb-4.1.0&q=85",
    icon: Heart,
  },
  {
    title: "Relationship Guidance",
    description:
      "Find clarity and support for your relationship through Islamic spirituality. Our compassionate advice helps you navigate challenges and build a stronger bond.",
    image:
      "https://images.unsplash.com/photo-1758894982901-c018b4bb5647?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxtdXNsaW0lMjBjb3VwbGUlMjBsb3ZlJTIwcm9tYW50aWN8ZW58MHx8fHwxNzcyNDY1Nzc4fDA&ixlib=rb-4.1.0&q=85",
    icon: Heart,
  },
  {
    title: "Personal Guidance",
    description:
      "Get effective spiritual support and personal advice rooted in Islamic teachings. Our consultations help restore balance and direction in your life.",
    image:
      "https://images.unsplash.com/photo-1762709414221-f8011b8ffcef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxtdXNsaW0lMjBjb3VwbGUlMjBsb3ZlJTIwcm9tYW50aWN8ZW58MHx8fHwxNzcyNDY1Nzc4fDA&ixlib=rb-4.1.0&q=85",
    icon: Sparkles,
  },
  {
    title: "Spiritual Consultation",
    description:
      "Seek clarity through focused Islamic duas and spiritual practices. Our dedicated consultations provide meaningful support for your emotional and spiritual well-being.",
    image:
      "https://images.unsplash.com/photo-1764641697973-1e55796073bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxpc2xhbWljJTIwbWFycmlhZ2UlMjBjZXJlbW9ueXxlbnwwfHx8fDE3NzI0NjU3Nzd8MA&ixlib=rb-4.1.0&q=85",
    icon: Moon,
  },
  {
    title: "Reconciliation Advice",
    description:
      "Reconnect with those you care about through compassionate Islamic guidance. Heal misunderstandings and bring reconciliation in a positive, supportive way.",
    image:
      "https://images.unsplash.com/photo-1762709414630-e56fe90beafd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxtdXNsaW0lMjBjb3VwbGUlMjB3ZWRkaW5nfGVufDB8fHx8MTc3MjQ2NTc3Nnww&ixlib=rb-4.1.0&q=85",
    icon: Heart,
  },
  {
    title: "Family Advice",
    description:
      "Restore peace and harmony in your family with compassionate spiritual support. Bring positivity and understanding among family members through Islamic teachings.",
    image:
      "https://images.unsplash.com/photo-1758314243385-9af94851a559?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxpc2xhbWljJTIwbWFycmlhZ2UlMjBjZXJlbW9ueXxlbnwwfHx8fDE3NzI0NjU3Nzd8MA&ixlib=rb-4.1.0&q=85",
    icon: Users,
  },
];

// ─── Services ────────────────────────────────────────────────────────────────
const Services = ({ openWhatsApp }) => {
  return (
    <section
      id="services"
      className="relative py-24 md:py-32 bg-background"
      data-testid="services-section"
    >
      <div className="absolute inset-0 star-pattern" />

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary mb-4">
            Our Services
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground">
            Spiritual Solutions for Life's Challenges
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {servicesData.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="service-card group relative overflow-hidden bg-card border border-white/5 rounded-xl"
              data-testid={`service-card-${index}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="service-image w-full h-full object-cover transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-primary/20 backdrop-blur-md flex items-center justify-center">
                  <service.icon className="w-5 h-5 text-primary" />
                </div>
              </div>

              <div className="p-6">
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                <button
                  onClick={openWhatsApp}
                  className="inline-flex items-center gap-2 text-primary text-sm font-medium hover:gap-3 transition-all"
                >
                  Talk to Expert Today <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ─── Testimonials Data ───────────────────────────────────────────────────────
const testimonialsData = [
  {
    name: "Ahmed Al-Rashid",
    location: "Dubai, UAE",
    text: "I'm grateful that I found Maulana Zakir Ali when I was going through a difficult time. His guidance and spiritual support helped me gain clarity and find a way forward. Truly a compassionate advisor.",
    image:
      "https://images.unsplash.com/photo-1647535993927-bcf896be066b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxhcmFiJTIwbXVzbGltJTIwbWFuJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzcyNTE4NjA2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Fatima Al-Sayed",
    location: "Abu Dhabi, UAE",
    text: "When I was deeply troubled about my personal situation, Maulana ji listened with patience and helped me understand my situation better. His advice gave me a new perspective and much needed peace of mind.",
    image:
      "https://images.unsplash.com/photo-1673547991911-c8b25dde4b9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxtdXNsaW0lMjB3b21hbiUyMGhpamFiJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzcyNTE4NjA2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Khalid Mohammed",
    location: "Sharjah, UAE",
    text: "Maulana Zakir Ali has a deep understanding of Islamic teachings. His sincere advice and duas helped me find clarity and work towards reconciling with my family after a long period of conflict.",
    image:
      "https://images.unsplash.com/photo-1766334079470-7f36e9c78311?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxhcmFiJTIwbXVzbGltJTIwbWFuJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzcyNTE4NjA2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Mariam Hassan",
    location: "Ajman, UAE",
    text: "I was unsure at first, but Maulana ji's compassionate guidance helped me understand my situation better. He supported me in finding clarity during the most confusing time of my life.",
    image:
      "https://images.unsplash.com/photo-1618305832468-d41088376681?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtdXNsaW0lMjB3b21hbiUyMGhpamFiJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzcyNTE4NjA2fDA&ixlib=rb-4.1.0&q=85",
  },
];

// ─── Testimonials ────────────────────────────────────────────────────────────
const Testimonials = () => (
  <section
    id="testimonials"
    className="relative py-24 md:py-32 bg-card"
    data-testid="testimonials-section"
  >
    <div className="absolute inset-0 islamic-pattern" />
    <div className="container mx-auto px-4 md:px-8 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary mb-4">
          Testimonials
        </p>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground">
          What Our Clients Say
        </h2>
      </motion.div>

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid md:grid-cols-2 gap-6 md:gap-8"
      >
        {testimonialsData.map((testimonial, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
            className="testimonial-card rounded-xl p-6 md:p-8"
            data-testid={`testimonial-card-${index}`}
          >
            <Quote className="w-10 h-10 text-primary/30 mb-4" />
            <p className="text-foreground leading-relaxed mb-6 italic font-accent">
              "{testimonial.text}"
            </p>
            <div className="flex items-center gap-4">
              <img
                src={testimonial.image}
                alt={testimonial.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-primary/30"
              />
              <div>
                <p className="font-heading text-foreground font-medium">
                  {testimonial.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {testimonial.location}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

// ─── FAQ Data ────────────────────────────────────────────────────────────────
const faqData = [
  {
    question: "What services does Maulana Zakir Ali offer?",
    answer:
      "Maulana Zakir Ali specializes in providing spiritual guidance for relationship challenges, family disputes, career challenges, financial concerns, and marriage issues using compassionate Islamic spiritual consultations including duas and sincere advice.",
  },
  {
    question: "How can I consult with Maulana Zakir Ali?",
    answer:
      "You can book a consultation by calling directly or through WhatsApp. Sessions are available both in-person and online for clients worldwide.",
  },
  {
    question: "Do you guarantee results?",
    answer:
      "No. This service provides spiritual guidance and consultation only. Results may vary depending on individual circumstances. Our goal is to offer sincere support and clarity through Islamic teachings, but we do not promise or guarantee any specific outcome.",
  },
  {
    question: "Does Maulana Zakir Ali offer privacy in consultations?",
    answer:
      "Absolutely. All consultations are 100% confidential. Your personal concerns and details remain completely private and are never shared with anyone.",
  },
  {
    question: "How does the consultation process work?",
    answer:
      "You can reach out via call or WhatsApp to schedule your session. During the consultation, Maulana Zakir Ali will listen to your concerns, offer Islamic perspective and spiritual guidance, and suggest appropriate duas or practices. Each session is handled with care and respect.",
  },
  {
    question: "Can Maulana Zakir Ali help with urgent concerns?",
    answer:
      "Yes, he is available to provide timely support for pressing life situations. Reach out directly via call or WhatsApp for prompt assistance.",
  },
];

// ─── FAQ ─────────────────────────────────────────────────────────────────────
const FAQ = () => (
  <section
    id="faq"
    className="relative py-24 md:py-32 bg-background"
    data-testid="faq-section"
  >
    <div className="absolute inset-0 star-pattern" />
    <div className="container mx-auto px-4 md:px-8 relative z-10">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary mb-4">
            FAQ
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Have questions about our spiritual guidance services? Find answers
            to common queries below. For any other questions, feel free to reach
            out directly.
          </p>
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1585036156171-384164a8c675?q=80&w=800&auto=format&fit=crop"
              alt="Islamic Prayer"
              className="w-full object-cover aspect-video"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-white/10 rounded-xl px-6 bg-card/50 backdrop-blur-sm"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-heading text-foreground hover:text-primary py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </div>
  </section>
);

// ─── Other Services Data ─────────────────────────────────────────────────────
const otherServicesData = [
  {
    title: "Marriage Advice for Couples",
    image:
      "https://images.unsplash.com/photo-1758141069056-986f9599adee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxpc2xhbWljJTIwbWFycmlhZ2UlMjBjZXJlbW9ueXxlbnwwfHx8fDE3NzI0NjU3Nzd8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Relationship Consultation",
    image:
      "https://images.unsplash.com/photo-1762709414656-994e961690f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxtdXNsaW0lMjBjb3VwbGUlMjB3ZWRkaW5nfGVufDB8fHx8MTc3MjQ2NTc3Nnww&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Marriage Problem Consultation",
    image:
      "https://images.unsplash.com/photo-1762708551197-24c74ea894b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxtdXNsaW0lMjBjb3VwbGUlMjB3ZWRkaW5nfGVufDB8fHx8MTc3MjQ2NTc3Nnww&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Breakup & Separation Support",
    image:
      "https://images.unsplash.com/photo-1758894982901-c018b4bb5647?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxtdXNsaW0lMjBjb3VwbGUlMjBsb3ZlJTIwcm9tYW50aWN8ZW58MHx8fHwxNzcyNDY1Nzc4fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Marital Harmony Advice",
    image:
      "https://images.unsplash.com/photo-1762709414221-f8011b8ffcef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxtdXNsaW0lMjBjb3VwbGUlMjBsb3ZlJTIwcm9tYW50aWN8ZW58MHx8fHwxNzcyNDY1Nzc4fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Relationship Support",
    image:
      "https://images.unsplash.com/photo-1764641697973-1e55796073bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxpc2xhbWljJTIwbWFycmlhZ2UlMjBjZXJlbW9ueXxlbnwwfHx8fDE3NzI0NjU3Nzd8MA&ixlib=rb-4.1.0&q=85",
  },
];

// ─── Other Services ───────────────────────────────────────────────────────────
const OtherServices = ({ openWhatsApp }) => (
  <section
    className="relative py-24 md:py-32 bg-card"
    data-testid="other-services-section"
  >
    <div className="absolute inset-0 islamic-pattern" />
    <div className="container mx-auto px-4 md:px-8 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary mb-4">
          More Services
        </p>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground">
          Other Spiritual Services
        </h2>
      </motion.div>

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
      >
        {otherServicesData.map((service, index) => (
          <motion.button
            key={index}
            variants={fadeInUp}
            onClick={openWhatsApp}
            className="group relative overflow-hidden rounded-xl aspect-square text-left"
            data-testid={`other-service-${index}`}
          >
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
              <h3 className="font-heading text-sm md:text-lg font-medium text-foreground group-hover:text-primary transition-colors">
                {service.title}
              </h3>
              <p className="text-xs text-primary mt-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Talk to Expert Today <ArrowRight className="w-3 h-3" />
              </p>
            </div>
          </motion.button>
        ))}
      </motion.div>
    </div>
  </section>
);

// ─── Service List ─────────────────────────────────────────────────────────────
const ServiceList = ({ openWhatsApp }) => {
  const serviceCategories = [
    {
      title: "Marriage Consultation",
      items: [
        "Delayed Marriage Advice",
        "Intercaste Marriage Consultation",
        "Relationship Guidance for Couples",
        "Reconciliation Support",
        "Child & Family Consultation",
      ],
    },
    {
      title: "Relationship Support",
      items: [
        "Relationship Clarity Support",
        "Guidance for Couples",
        "Relationship Advice",
        "Compassionate Support Services",
        "Spiritual Consultation for Relationships",
      ],
    },
    {
      title: "Personal & Family Guidance",
      items: [
        "Intercaste Relationship Advice",
        "Marital Harmony Consultation",
        "Family Relationship Support",
        "Marriage Advice",
        "Spousal Relationship Guidance",
      ],
    },
  ];

  return (
    <section
      className="relative py-24 md:py-32 bg-background"
      data-testid="service-list-section"
    >
      <div className="absolute inset-0 star-pattern" />
      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary mb-4">
            Complete Services
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground">
            All Spiritual Consultation Services
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-6 md:gap-8"
        >
          {serviceCategories.map((category, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-card border border-white/5 rounded-xl p-6 md:p-8"
              data-testid={`service-category-${index}`}
            >
              <h3 className="font-heading text-xl text-primary mb-6">
                {category.title}
              </h3>
              <ul className="space-y-3">
                {category.items.map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-center gap-3 text-muted-foreground mt-3"
                  >
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={openWhatsApp}
                className="whatsapp-btn inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-sm font-semibold mt-6 transition-all"
              >
                <MessageCircle className="w-4 h-4" />
                Online Chat
              </button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ─── Contact ──────────────────────────────────────────────────────────────────
const Contact = ({ openCall, openWhatsApp }) => {
  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 bg-card"
      data-testid="contact-section"
    >
      <div className="absolute inset-0 islamic-pattern" />
      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary mb-4">
              Contact Us
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground mb-6">
              Talk to Expert Today
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-10 max-w-2xl mx-auto">
              Reach out for spiritual consultation and compassionate advice.
              Whether you're facing relationship challenges, family concerns, or
              personal difficulties, {CONTACT.name} is here to support you.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto"
          >
            {/* Call Card */}
            <button
              onClick={openCall}
              className="group bg-background border border-primary/20 rounded-2xl p-6 hover:border-primary/50 transition-all hover:-translate-y-1 text-center"
              data-testid="contact-call-card"
            >
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Phone className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-lg text-foreground mb-2">
                Call Us
              </h3>
              <p className="text-primary font-semibold text-sm">India</p>
              <p className="text-xs text-muted-foreground mt-1">
                Tap to choose number
              </p>
            </button>

            {/* WhatsApp Card */}
            <button
              onClick={openWhatsApp}
              className="group bg-background border border-[#25D366]/20 rounded-2xl p-6 hover:border-[#25D366]/50 transition-all hover:-translate-y-1 text-center"
              data-testid="contact-whatsapp-card"
            >
              <div className="w-14 h-14 rounded-full bg-[#25D366]/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <MessageCircle className="w-6 h-6 text-[#25D366]" />
              </div>
              <h3 className="font-heading text-lg text-foreground mb-2">
                WhatsApp
              </h3>
              <p className="text-[#25D366] font-semibold text-sm">
                India
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Tap to choose number
              </p>
            </button>

            {/* Email Card */}
            <a
              href={`mailto:${CONTACT.email}`}
              className="group bg-background border border-blue-500/20 rounded-2xl p-6 hover:border-blue-500/50 transition-all hover:-translate-y-1 text-center"
              data-testid="contact-email-card"
            >
              <div className="w-14 h-14 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Mail className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="font-heading text-lg text-foreground mb-2">
                Email Us
              </h3>
              <p className="text-blue-500 font-semibold text-xs break-all">
                {CONTACT.email}
              </p>
            </a>
          </motion.div>

          {/* Name Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-12 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-8 max-w-2xl mx-auto"
          >
            <Moon className="w-10 h-10 text-primary mx-auto mb-4" />
            <h3 className="font-heading text-2xl text-primary mb-2">
              {CONTACT.name}
            </h3>
            <p className="text-muted-foreground mb-4">{CONTACT.title}</p>

            <div className="flex items-center justify-center gap-4 mt-4">
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1877F2]/20 flex items-center justify-center hover:bg-[#1877F2]/30 transition-colors"
                aria-label="Facebook"
                data-testid="contact-facebook"
              >
                <Facebook className="w-5 h-5 text-[#1877F2]" />
              </a>
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#E4405F]/20 flex items-center justify-center hover:bg-[#E4405F]/30 transition-colors"
                aria-label="Instagram"
                data-testid="contact-instagram"
              >
                <Instagram className="w-5 h-5 text-[#E4405F]" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ─── Footer ───────────────────────────────────────────────────────────────────
const Footer = ({ openCall, openWhatsApp }) => (
  <footer
    className="bg-background border-t border-white/5 py-8"
    data-testid="footer"
  >
    <div className="container mx-auto px-4 md:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Moon className="w-5 h-5 text-primary" />
          <span className="font-heading text-primary">Nur-e-Hidayat</span>
        </div>
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} {CONTACT.name}. All Rights Reserved.
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            This service provides spiritual guidance only. No guaranteed
            outcomes.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={openCall}
            className="text-muted-foreground hover:text-primary transition-colors"
            aria-label="Call"
          >
            <Phone className="w-5 h-5" />
          </button>
          <button
            onClick={openWhatsApp}
            className="text-muted-foreground hover:text-[#25D366] transition-colors"
            aria-label="WhatsApp"
          >
            <MessageCircle className="w-5 h-5" />
          </button>
          <a
            href={CONTACT.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-[#1877F2] transition-colors"
            aria-label="Facebook"
            data-testid="footer-facebook"
          >
            <Facebook className="w-5 h-5" />
          </a>
          <a
            href={CONTACT.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-[#E4405F] transition-colors"
            aria-label="Instagram"
            data-testid="footer-instagram"
          >
            <Instagram className="w-5 h-5" />
          </a>
        </div>
      </div>
    </div>
  </footer>
);

// ─── Floating WhatsApp ────────────────────────────────────────────────────────
const FloatingWhatsApp = ({ openWhatsApp }) => (
  <button
    onClick={openWhatsApp}
    className="fixed bottom-6 right-6 z-50 whatsapp-btn p-4 rounded-full text-white pulse-gold"
    data-testid="floating-whatsapp"
    aria-label="Chat on WhatsApp"
  >
    <MessageCircle className="w-6 h-6" />
  </button>
);

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  const { openCall, openWhatsApp, ModalComponent } = useContactPicker();

  return (
    <div className="App min-h-screen bg-background">
      <div className="noise-overlay" />

      {/* Global modal rendered at root level */}
      {ModalComponent}

      <Navbar openCall={openCall} openWhatsApp={openWhatsApp} />

      <main>
        <Hero openCall={openCall} openWhatsApp={openWhatsApp} />
        <UrgencyBlock openWhatsApp={openWhatsApp} />
        <About openWhatsApp={openWhatsApp} />
        <Services openWhatsApp={openWhatsApp} />
        <Testimonials />
        <OtherServices openWhatsApp={openWhatsApp} />
        <ServiceList openWhatsApp={openWhatsApp} />
        <FAQ />
        <Contact openCall={openCall} openWhatsApp={openWhatsApp} />
      </main>

      <Footer openCall={openCall} openWhatsApp={openWhatsApp} />
      <FloatingWhatsApp openWhatsApp={openWhatsApp} />
    </div>
  );
}

export default App;
